/**
 * Meta Tags Generator
 * Estandariza la generación de meta tags para todas las páginas
 * Asegura longitudes óptimas y consistencia SEO
 */

interface MetaTagsConfig {
  pageType: 'home' | 'city' | 'country' | 'specialty' | 'therapist' | 'find' | 'about' | 'contact' | 'pricing' | 'static';
  entityData?: any;
  customTitle?: string;
  customDescription?: string;
  customKeywords?: string[];
}

interface GeneratedMetaTags {
  title: string;
  description: string;
  keywords: string;
  h1: string;
  canonicalUrl: string;
  ogImage: string;
  ogType: string;
}

const BASE_URL = 'https://psychotherapist.help';
const SITE_NAME = 'Global Therapy Connect';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;

// Límites óptimos de caracteres
const LIMITS = {
  title: { min: 50, max: 60 },
  description: { min: 150, max: 160 },
  h1: { min: 20, max: 70 }
};

/**
 * Trunca texto a longitud máxima manteniendo palabras completas
 */
const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  
  const truncated = text.slice(0, maxLength - 3);
  const lastSpace = truncated.lastIndexOf(' ');
  
  return lastSpace > 0 ? truncated.slice(0, lastSpace) + '...' : truncated + '...';
};

/**
 * Optimiza el título para SEO (50-60 caracteres)
 */
const optimizeTitle = (title: string, siteName: string = SITE_NAME): string => {
  const fullTitle = `${title} | ${siteName}`;
  
  if (fullTitle.length <= LIMITS.title.max) {
    return fullTitle;
  }
  
  // Si es muy largo, truncar el título pero mantener el site name
  const maxTitleLength = LIMITS.title.max - siteName.length - 3; // -3 para " | "
  const truncatedTitle = truncateText(title, maxTitleLength);
  
  return `${truncatedTitle} | ${siteName}`;
};

/**
 * Optimiza la descripción para SEO (150-160 caracteres)
 */
const optimizeDescription = (description: string): string => {
  if (description.length >= LIMITS.description.min && description.length <= LIMITS.description.max) {
    return description;
  }
  
  if (description.length < LIMITS.description.min) {
    return description; // Permitir descripciones cortas si son precisas
  }
  
  return truncateText(description, LIMITS.description.max);
};

/**
 * Genera keywords optimizados basados en el tipo de página
 */
const generateKeywords = (pageType: string, entityData?: any, custom?: string[]): string => {
  const baseKeywords = [
    'psychotherapy',
    'mental health',
    'therapy',
    'counseling',
    'psychologist',
    'psychiatrist',
    'online therapy',
    'mental health services'
  ];
  
  let specificKeywords: string[] = [];
  
  switch (pageType) {
    case 'city':
      if (entityData?.name && entityData?.country) {
        const cityName = entityData.name.replace(/\s+\d+$/, '').trim();
        specificKeywords = [
          `therapist in ${cityName}`,
          `therapy ${cityName}`,
          `mental health ${cityName}`,
          `counseling ${cityName}`,
          `psychologist ${cityName}`,
          `${cityName} ${entityData.country} therapy`,
          `best therapist ${cityName}`,
          `online therapy ${cityName}`
        ];
      }
      break;
      
    case 'country':
      if (entityData?.name) {
        specificKeywords = [
          `therapist in ${entityData.name}`,
          `therapy ${entityData.name}`,
          `mental health ${entityData.name}`,
          `${entityData.name} psychologists`,
          `counseling services ${entityData.name}`
        ];
      }
      break;
      
    case 'specialty':
      if (entityData?.name) {
        specificKeywords = [
          `${entityData.name} therapy`,
          `${entityData.name} treatment`,
          `${entityData.name} counseling`,
          `therapist for ${entityData.name}`
        ];
      }
      break;
      
    case 'find':
      specificKeywords = [
        'find therapist',
        'find psychologist',
        'therapist near me',
        'find mental health professional',
        'search therapist',
        'locate counselor'
      ];
      break;
      
    case 'about':
      specificKeywords = [
        'about therapy platform',
        'mental health directory',
        'global therapy network',
        'Dr. José Luis Marín',
        'therapy training'
      ];
      break;
  }
  
  const allKeywords = [...specificKeywords, ...baseKeywords, ...(custom || [])];
  
  // Eliminar duplicados y limitar a 15 keywords
  return [...new Set(allKeywords)].slice(0, 15).join(', ');
};

/**
 * Genera meta tags para páginas de ciudad
 */
const generateCityMetaTags = (city: any): Partial<GeneratedMetaTags> => {
  const cityName = city.name?.replace(/\s+\d+$/, '').trim() || 'Unknown City';
  
  return {
    title: `Professional Therapists in ${cityName}`,
    description: `Find qualified therapists in ${cityName}, ${city.country}. Licensed mental health professionals for therapy, counseling & psychological services. Online & in-person.`,
    h1: `Professional Psychotherapists in ${cityName}`,
    canonicalUrl: `${BASE_URL}/city/${city.slug}`,
    ogImage: `${BASE_URL}/images/cities/${city.slug}-therapy.webp`,
    ogType: 'place'
  };
};

/**
 * Genera meta tags para páginas de país
 */
const generateCountryMetaTags = (country: any): Partial<GeneratedMetaTags> => {
  return {
    title: `Mental Health Services in ${country.name}`,
    description: `Find licensed therapists throughout ${country.name}. Professional mental health services, online therapy, and in-person counseling. Connect with verified psychologists.`,
    h1: `Therapists and Mental Health Services in ${country.name}`,
    canonicalUrl: `${BASE_URL}/psychologists/${country.slug}`,
    ogImage: `${BASE_URL}/images/countries/${country.slug}-therapy.webp`,
    ogType: 'website'
  };
};

/**
 * Genera meta tags para páginas de especialidad
 */
const generateSpecialtyMetaTags = (specialty: any): Partial<GeneratedMetaTags> => {
  return {
    title: `${specialty.name} Therapy & Treatment`,
    description: `Find specialized therapists for ${specialty.name}. Professional treatment and counseling from licensed mental health experts. Online and in-person sessions available worldwide.`,
    h1: `${specialty.name} Therapy and Counseling`,
    canonicalUrl: `${BASE_URL}/specialties/${specialty.slug}`,
    ogImage: `${BASE_URL}/images/specialties/${specialty.slug}.webp`,
    ogType: 'website'
  };
};

/**
 * Genera meta tags para la página de inicio
 */
const generateHomeMetaTags = (): Partial<GeneratedMetaTags> => {
  return {
    title: 'Find Professional Therapists Worldwide',
    description: 'Connect with licensed therapists globally. Online & in-person therapy sessions. Expert mental health professionals for anxiety, depression, couples therapy & more.',
    h1: 'Find Professional Therapists Worldwide',
    canonicalUrl: BASE_URL,
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website'
  };
};

/**
 * Genera meta tags para la página de búsqueda
 */
const generateFindMetaTags = (): Partial<GeneratedMetaTags> => {
  return {
    title: 'Find Your Perfect Therapist',
    description: 'Search our global directory of licensed mental health professionals. Filter by location, specialty, language & availability. Find the right therapist for your needs.',
    h1: 'Find Your Perfect Mental Health Professional',
    canonicalUrl: `${BASE_URL}/find`,
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website'
  };
};

/**
 * Genera meta tags para la página About
 */
const generateAboutMetaTags = (): Partial<GeneratedMetaTags> => {
  return {
    title: 'About Global Therapy Connect',
    description: 'Learn about our mission to democratize mental health worldwide. Founded by Dr. José Luis Marín, connecting patients with verified therapists globally.',
    h1: 'About Global Therapy Connect',
    canonicalUrl: `${BASE_URL}/about`,
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website'
  };
};

/**
 * Genera meta tags para la página de contacto
 */
const generateContactMetaTags = (): Partial<GeneratedMetaTags> => {
  return {
    title: 'Contact Us - Global Therapy Connect',
    description: 'Get in touch with our team. Questions about our therapy platform, therapist directory, or mental health services? Contact us for support.',
    h1: 'Contact Global Therapy Connect',
    canonicalUrl: `${BASE_URL}/contact`,
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website'
  };
};

/**
 * Genera meta tags para la página de precios
 */
const generatePricingMetaTags = (): Partial<GeneratedMetaTags> => {
  return {
    title: 'Therapy Pricing - Affordable Mental Health',
    description: 'Transparent therapy pricing. Find affordable mental health services worldwide. Compare therapist rates, insurance options, and payment plans.',
    h1: 'Transparent Therapy Pricing',
    canonicalUrl: `${BASE_URL}/pricing`,
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website'
  };
};

/**
 * Función principal para generar meta tags
 */
export const generateMetaTags = (config: MetaTagsConfig): GeneratedMetaTags => {
  let generated: Partial<GeneratedMetaTags> = {};
  
  // Generar tags basados en el tipo de página
  switch (config.pageType) {
    case 'city':
      generated = generateCityMetaTags(config.entityData);
      break;
    case 'country':
      generated = generateCountryMetaTags(config.entityData);
      break;
    case 'specialty':
      generated = generateSpecialtyMetaTags(config.entityData);
      break;
    case 'home':
      generated = generateHomeMetaTags();
      break;
    case 'find':
      generated = generateFindMetaTags();
      break;
    case 'about':
      generated = generateAboutMetaTags();
      break;
    case 'contact':
      generated = generateContactMetaTags();
      break;
    case 'pricing':
      generated = generatePricingMetaTags();
      break;
    default:
      generated = {
        title: config.customTitle || 'Global Therapy Connect',
        description: config.customDescription || 'Find professional mental health services worldwide',
        h1: config.customTitle || 'Mental Health Services',
        canonicalUrl: BASE_URL,
        ogImage: DEFAULT_OG_IMAGE,
        ogType: 'website'
      };
  }
  
  // Aplicar custom overrides si existen
  if (config.customTitle) generated.title = config.customTitle;
  if (config.customDescription) generated.description = config.customDescription;
  
  // Optimizar título y descripción
  const optimizedTitle = optimizeTitle(generated.title || 'Global Therapy Connect');
  const optimizedDescription = optimizeDescription(generated.description || 'Find professional mental health services worldwide');
  
  // Generar keywords
  const keywords = generateKeywords(config.pageType, config.entityData, config.customKeywords);
  
  return {
    title: optimizedTitle,
    description: optimizedDescription,
    keywords,
    h1: generated.h1 || generated.title || 'Mental Health Services',
    canonicalUrl: generated.canonicalUrl || BASE_URL,
    ogImage: generated.ogImage || DEFAULT_OG_IMAGE,
    ogType: generated.ogType || 'website'
  };
};

/**
 * Valida si los meta tags cumplen con las mejores prácticas SEO
 */
export const validateMetaTags = (tags: GeneratedMetaTags): { isValid: boolean; warnings: string[] } => {
  const warnings: string[] = [];
  
  // Validar longitud del título
  const titleLength = tags.title.length;
  if (titleLength < LIMITS.title.min) {
    warnings.push(`Title is too short (${titleLength} chars). Recommended: ${LIMITS.title.min}-${LIMITS.title.max}`);
  } else if (titleLength > LIMITS.title.max) {
    warnings.push(`Title is too long (${titleLength} chars). Recommended: ${LIMITS.title.min}-${LIMITS.title.max}`);
  }
  
  // Validar longitud de la descripción
  const descLength = tags.description.length;
  if (descLength < LIMITS.description.min) {
    warnings.push(`Description is too short (${descLength} chars). Recommended: ${LIMITS.description.min}-${LIMITS.description.max}`);
  } else if (descLength > LIMITS.description.max) {
    warnings.push(`Description is too long (${descLength} chars). Recommended: ${LIMITS.description.min}-${LIMITS.description.max}`);
  }
  
  // Validar que existan keywords
  if (!tags.keywords || tags.keywords.length < 10) {
    warnings.push('Keywords are missing or too short');
  }
  
  return {
    isValid: warnings.length === 0,
    warnings
  };
};
